import React, { useMemo } from 'react';
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RansomwareItem } from '@/types/ransomware';
import { useTheme } from '@/hooks/useTheme';
import { useLanguage } from '@/hooks/useLanguage';
import { subMonths, format, startOfMonth, isSameMonth } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { getCountryISO2 } from '@/utils/countryMapping';

interface RansomwareStatsProps {
    data: RansomwareItem[];
    selectedCountry: string | null;
    selectedGroup?: string | null;
}

const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#84cc16', '#06b6d4', '#6366f1', '#8b5cf6', '#d946ef'];

export const RansomwareStats: React.FC<RansomwareStatsProps> = ({ data, selectedCountry, selectedGroup }) => {
    const { theme } = useTheme();
    const { language } = useLanguage();
    const navigate = useNavigate();

    const isDark = theme === 'dark';
    const textColor = isDark ? '#e5e7eb' : '#374151';
    const gridColor = isDark ? '#374151' : '#e5e7eb';

    // Filter out '??' country globally
    const filteredData = useMemo(() => {
        return data.filter(item => item.country !== '??');
    }, [data]);

    // 1. Pie Chart: Most Active Groups OR Most Attacked Countries (if group selected)
    const pieChartData = useMemo(() => {
        const targetData = selectedCountry
            ? filteredData.filter(item => item.country === selectedCountry)
            : filteredData;

        const counts: Record<string, number> = {};

        if (selectedGroup) {
            // If group selected, show most attacked countries
            targetData.forEach(item => {
                const country = item.country || 'Unknown';
                counts[country] = (counts[country] || 0) + 1;
            });
        } else {
            // Otherwise show most active groups
            targetData.forEach(item => {
                const group = item.group || 'Unknown';
                counts[group] = (counts[group] || 0) + 1;
            });
        }

        const sorted = Object.entries(counts)
            .sort(([, a], [, b]) => b - a)
            .map(([name, value]) => ({ name, value }));

        // Show Top 10
        const top10 = sorted.slice(0, 10);
        const others = sorted.slice(10).reduce((acc, curr) => acc + curr.value, 0);

        if (others > 0) {
            return [...top10, { name: language === 'pl' ? 'Inne' : 'Others', value: others }];
        }
        return top10;
    }, [filteredData, selectedCountry, selectedGroup, language]);

    // 2. Attacks Last 12 Months (Bar Chart)
    const monthlyStats = useMemo(() => {
        const targetData = selectedCountry
            ? filteredData.filter(item => item.country === selectedCountry)
            : filteredData;

        const today = new Date();
        const last12Months = Array.from({ length: 12 }, (_, i) => {
            const d = subMonths(today, 11 - i);
            return startOfMonth(d);
        });

        return last12Months.map(monthDate => {
            const count = targetData.filter(item => {
                const itemDate = new Date(item.attackdate);
                return isSameMonth(itemDate, monthDate);
            }).length;

            return {
                name: format(monthDate, 'MMM', { locale: language === 'pl' ? pl : undefined }),
                fullDate: format(monthDate, 'MMMM yyyy', { locale: language === 'pl' ? pl : undefined }),
                count
            };
        });
    }, [filteredData, selectedCountry, language]);

    // 3. Line Chart: Top Countries Trend OR Top TLDs Trend (if group selected) OR Previous Years (if country selected)
    const trendStats = useMemo(() => {
        // CASE 1: Country Selected -> Show Previous Years (Yearly Stats)
        if (selectedCountry) {
            const currentYear = new Date().getFullYear();
            const years = Array.from({ length: 5 }, (_, i) => currentYear - 4 + i); // Last 5 years including current

            return {
                data: years.map(year => {
                    const count = filteredData.filter(item => {
                        const itemYear = new Date(item.attackdate).getFullYear();
                        return item.country === selectedCountry && itemYear === year;
                    }).length;

                    return {
                        name: year.toString(),
                        [selectedCountry]: count
                    };
                }),
                keys: [selectedCountry]
            };
        }

        // CASE 2: Group Selected -> Show Top TLDs
        let itemsToShow: string[] = [];
        const isTLDMode = !!selectedGroup;

        if (isTLDMode) {
            // Extract TLDs
            const tldCounts: Record<string, number> = {};
            filteredData.forEach(item => {
                if (!item.domain) return;
                let tld = '';
                try {
                    // Handle cases where domain might be a full URL or just a domain
                    const domainStr = item.domain.startsWith('http') ? item.domain : `http://${item.domain}`;
                    const url = new URL(domainStr);
                    const parts = url.hostname.split('.');
                    if (parts.length > 1) {
                        tld = '.' + parts[parts.length - 1];
                    } else {
                        tld = '.' + url.hostname;
                    }
                } catch (e) {
                    // Fallback for invalid URLs
                    const parts = item.domain.split('.');
                    if (parts.length > 1) {
                        tld = '.' + parts[parts.length - 1].split('/')[0]; // Ensure no path remains
                    } else {
                        tld = '.' + item.domain;
                    }
                }

                if (tld) {
                    tldCounts[tld] = (tldCounts[tld] || 0) + 1;
                }
            });

            itemsToShow = Object.entries(tldCounts)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([tld]) => tld);
        } else {
            // CASE 3: Default -> Show Top 5 Countries
            const countryCounts: Record<string, number> = {};
            filteredData.forEach(item => {
                if (!item.country) return;
                countryCounts[item.country] = (countryCounts[item.country] || 0) + 1;
            });

            itemsToShow = Object.entries(countryCounts)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([country]) => country);
        }

        // Prepare data for last 12 months
        const today = new Date();
        const last12Months = Array.from({ length: 12 }, (_, i) => {
            const d = subMonths(today, 11 - i);
            return startOfMonth(d);
        });

        return {
            data: last12Months.map(monthDate => {
                const stats: any = {
                    name: format(monthDate, 'MMM', { locale: language === 'pl' ? pl : undefined }),
                };

                itemsToShow.forEach(key => {
                    const count = filteredData.filter(item => {
                        const itemDate = new Date(item.attackdate);
                        const isMatch = isTLDMode
                            ? item.domain?.endsWith(key)
                            : item.country === key;

                        return isMatch && isSameMonth(itemDate, monthDate);
                    }).length;
                    stats[key] = count;
                });

                return stats;
            }),
            keys: itemsToShow
        };
    }, [filteredData, selectedCountry, selectedGroup, language]);

    const pieChartTitle = useMemo(() => {
        if (selectedGroup) return language === 'pl' ? 'Najczęściej atakowane kraje' : 'Most Attacked Countries';
        return language === 'pl' ? 'Najaktywniejsze Grupy' : 'Most Active Groups';
    }, [selectedGroup, language]);

    const lineChartTitle = useMemo(() => {
        if (selectedCountry) return language === 'pl' ? 'Poprzednie lata' : 'Previous Years';
        if (selectedGroup) return language === 'pl' ? 'Top 5 atakowanych domen (TLD)' : 'Top 5 Attacked TLDs';
        return language === 'pl' ? 'Trend dla Top 5 Krajów' : 'Trend for Top 5 Countries';
    }, [selectedCountry, selectedGroup, language]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Pie Chart */}
            <Card className="glass-card">
                <CardHeader>
                    <CardTitle className="text-lg font-medium">
                        {pieChartTitle}
                        {selectedCountry && <span className="text-sm font-normal text-muted-foreground ml-2">({selectedCountry})</span>}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieChartData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={2}
                                    dataKey="value"
                                    className="cursor-pointer outline-none"
                                    onClick={(data) => {
                                        if (data.name === 'Others' || data.name === 'Inne') return;

                                        if (!selectedGroup) {
                                            navigate(`/ransomware/group/${encodeURIComponent(data.name)}`);
                                        } else {
                                            const countryCode = getCountryISO2(data.name);
                                            if (countryCode) {
                                                navigate(`/ransomware/${countryCode.toLowerCase()}`);
                                            }
                                        }
                                    }}
                                >
                                    {pieChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} className="cursor-pointer hover:opacity-80 transition-opacity" />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ backgroundColor: isDark ? '#1f2937' : '#ffffff', borderColor: gridColor }}
                                    itemStyle={{ color: textColor }}
                                />
                                <Legend
                                    onClick={(data) => {
                                        // @ts-ignore - Recharts types are sometimes tricky with events
                                        const name = data.value;
                                        if (name === 'Others' || name === 'Inne') return;

                                        if (!selectedGroup) {
                                            navigate(`/ransomware/group/${encodeURIComponent(name)}`);
                                        } else {
                                            const countryCode = getCountryISO2(name);
                                            if (countryCode) {
                                                navigate(`/ransomware/${countryCode.toLowerCase()}`);
                                            }
                                        }
                                    }}
                                    wrapperStyle={{ cursor: 'pointer' }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Attacks Last 12 Months */}
            <Card className="glass-card">
                <CardHeader>
                    <CardTitle className="text-lg font-medium">
                        {language === 'pl' ? 'Ataki w ostatnich 12 miesiącach' : 'Attacks in Last 12 Months'}
                        {selectedCountry && <span className="text-sm font-normal text-muted-foreground ml-2">({selectedCountry})</span>}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={monthlyStats}>
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    stroke={textColor}
                                    fontSize={12}
                                    tickLine={false}
                                    axisLine={false}
                                    interval={0} // Force show all labels
                                    minTickGap={0}
                                />
                                <YAxis stroke={textColor} fontSize={12} tickLine={false} axisLine={false} />
                                <Tooltip
                                    cursor={{ fill: isDark ? '#374151' : '#f3f4f6' }}
                                    contentStyle={{ backgroundColor: isDark ? '#1f2937' : '#ffffff', borderColor: gridColor }}
                                    itemStyle={{ color: textColor }}
                                    labelStyle={{ color: textColor }}
                                />
                                <Bar dataKey="count" fill="#ef4444" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Trend Chart */}
            <Card className="glass-card">
                <CardHeader>
                    <CardTitle className="text-lg font-medium">
                        {lineChartTitle}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trendStats.data}>
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    stroke={textColor}
                                    fontSize={12}
                                    tickLine={false}
                                    axisLine={false}
                                    interval={0}
                                    minTickGap={0}
                                />
                                <YAxis stroke={textColor} fontSize={12} tickLine={false} axisLine={false} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: isDark ? '#1f2937' : '#ffffff', borderColor: gridColor }}
                                    itemStyle={{ color: textColor }}
                                    labelStyle={{ color: textColor }}
                                />
                                <Legend />
                                {trendStats.keys.map((key, index) => (
                                    <Line
                                        key={key}
                                        type="monotone"
                                        dataKey={key}
                                        stroke={COLORS[index % COLORS.length]}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 6 }}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
